import React, { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import contractingCompanyApi from '../../../app/api/contractingCompanyApi';

const CombineContractingCompaniesPage = () => {
  const auth = useAuth();
  const { account } = useSelector((state) => state.app);
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (account?.id) {
      loadCompanies();
    }
  }, [account]);

  const loadCompanies = async () => {
    try {
      const response = await contractingCompanyApi.getContractingCompanies(auth.user.access_token, account.id);
      setCompanies(response.data);
      if (!response.data || response.data.length === 0) {
        setError('No companies found for this account.');
      } else {
        setError(null);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      setError('Failed to load companies. Please check your connection or contact support.');
    }
  };

  const handleCompanySelect = (companyId) => {
    setSelectedCompanies(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      }
      return [...prev, companyId];
    });
  };

  const handleDelete = async (companyId, companyName) => {
    if (!window.confirm(`Are you sure you want to delete "${companyName}"?`)) {
      return;
    }

    setIsLoading(true);
    try {
      await contractingCompanyApi.deleteCompany(auth.user.access_token, account.id, companyId);
      await loadCompanies();
    } catch (error) {
      console.error('Error deleting company:', error);
      alert('Failed to delete company. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedCompanies.length < 2) {
      alert('Please select at least 2 companies to combine');
      return;
    }
    if (!newCompanyName.trim()) {
      alert('Please enter a new company name');
      return;
    }

    setIsLoading(true);
    try {
      await contractingCompanyApi.combineCompanies(auth.user.access_token, account.id, {
        companyIds: selectedCompanies,
        newCompanyName: newCompanyName.trim()
      });
      navigate('/sites');
    } catch (error) {
      console.error('Error combining companies:', error);
      alert('Failed to combine companies. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Combine Contracting Companies
        </Typography>
        <Button
          variant="outlined"
          onClick={loadCompanies}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </Box>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}
      
      <form onSubmit={handleSubmit}>
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Select Companies to Combine
          </Typography>
          <FormGroup>
            {companies.map(company => (
              <Box key={company.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCompanies.includes(company.id)}
                      onChange={() => handleCompanySelect(company.id)}
                    />
                  }
                  label={`${company.name} (Profiles: ${company.profileCount}, Skillko: ${company.skillkoProfileCount}, Not Enrolled: ${company.notEnrolledCount})`}
                  sx={{ flex: 1 }}
                />
                {company.profileCount === 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => handleDelete(company.id, company.name)}
                    disabled={isLoading}
                  >
                    Delete
                  </Button>
                )}
              </Box>
            ))}
          </FormGroup>
        </FormControl>

        <TextField
          fullWidth
          label="New Company Name"
          value={newCompanyName}
          onChange={(e) => setNewCompanyName(e.target.value)}
          sx={{ mb: 3 }}
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isLoading || selectedCompanies.length < 2 || !newCompanyName.trim()}
        >
          {isLoading ? 'Combining...' : 'Combine Companies'}
        </Button>
      </form>
    </Box>
  );
};

export default CombineContractingCompaniesPage; 